'use client';

/**
 * Three of the four My Library tabs that aren't the file manager:
 *   - AgentsTab    → wraps /api/users/me/agents (server-side search/sort/scope)
 *   - PostsTab     → wraps /api/users/me/posts  (extended for q/scope/mediaType/sort)
 *   - ChannelsTab  → wraps /api/channels (scope_following=<me>, scope_mine, etc.)
 *
 * Each tab owns its own data + filter state. The shared `FilterRail`
 * primitive renders the search/chip/sort/bulk-action UI; rows are
 * rendered locally because each tab has a different domain shape.
 *
 * The Content tab is intentionally NOT here — it lives in
 * `my-content-manager.tsx` because it carries enough extra UI
 * (folders, breadcrumbs, drop targets, uploads) to be unwieldy as
 * a peer of the simpler tabs.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
} from 'react';
import {
  Trash2,
  Share2,
  ExternalLink,
  LogIn,
  LogOut,
  Users,
  FileText,
  Heart,
  MessageSquare,
  Hash,
  TrendingUp,
  Star,
  Plus,
  Settings,
} from 'lucide-react';
import { useReactFlow } from '@xyflow/react';
import { useT } from '@/components/i18n-provider';
import { useFullscreenNav } from '@/components/workspace/fullscreen-nav';
import { useViewMode, ViewModeToggle, cardGridClass, cardGridStyle, ListingBadge } from './view-mode';
import { coverGradient } from '@/lib/marketplace/cover';
import { AgentSettingsPanel } from '@/components/agent-details';
import { ChevronLeft, MessageCircle, MessageSquarePlus } from 'lucide-react';
import { AppStoreHeader } from '@/components/marketplace/app-store-header';
import { ChannelFeed } from '@/components/marketplace/channel-feed';
import { ChatView } from '@/components/chat/chat-view';

/** DM chats with one agent (the Chats tab). Open one → it opens on the canvas. */
function AgentChatsList({ agentId, onOpen, onNew }: { agentId: string; onOpen: (chatId: string) => void; onNew: () => void }) {
  const t = useT();
  const [chats, setChats] = useState<{ id: string; name: string | null; lastMessage?: string }[]>([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let active = true;
    fetch(`/api/messages?agentId=${agentId}&type=dm`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d?.chats) setChats(d.chats); })
      .catch(() => {})
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [agentId]);
  return (
    <div className="p-3">
      <button
        type="button"
        onClick={onNew}
        className="mb-3 inline-flex items-center gap-1.5 rounded-lg bg-foreground px-3 py-1.5 text-[13px] font-medium text-background hover:opacity-90"
      >
        <MessageSquarePlus className="h-3.5 w-3.5" />
        {t('workspace.panels.newChat')}
      </button>
      {loading ? (
        <p className="py-4 text-center text-xs text-muted-foreground">Loading…</p>
      ) : chats.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">{t('workspace.panels.noChatsWithAgent')}</p>
      ) : (
        <div className="flex flex-col gap-1">
          {chats.map((c) => (
            <button
              key={c.id}
              type="button"
              onClick={() => onOpen(c.id)}
              className="flex flex-col items-start rounded-lg px-3 py-2 text-left transition-colors hover:bg-accent"
            >
              <span className="truncate text-[13px] font-medium">{c.name || 'Chat'}</span>
              {c.lastMessage && <span className="line-clamp-1 text-[11px] text-muted-foreground">{c.lastMessage}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
import { Avatar } from '@/components/avatar';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/utils';
import { shareLink } from '@/lib/share';
import {
  useWorkspaceActions,
  useWorkspaceItems,
} from '@/components/workspace/store';
import { useAuth } from '@/components/auth-provider';
import { onWorkspaceItemsChanged } from '@/lib/workspaces/list-events';
import { useRailV2 } from '@/lib/workspaces/rail-flag';
import { useFavorites, sortByFavorite } from '@/lib/workspaces/favorites';
import { FilterRail } from './filter-rail';
import {
  FileContextMenu,
  useFileContextMenu,
  type ContextMenuItem,
} from './file-context-menu';

const PAGE_LIMIT = 30;

/**
 * Returns a counter that increments whenever an item is added to the
 * current workspace (`workspace:items-changed`). Tabs include it in
 * their fetch-effect deps so a freshly created agent / post / content
 * surfaces in the list live, without a page refresh.
 */
function useItemsChangedNonce(): number {
  const [n, setN] = useState(0);
  useEffect(() => onWorkspaceItemsChanged(() => setN((v) => v + 1)), []);
  return n;
}

// ===========================================================================
// AgentsTab
// ===========================================================================

type AgentRow = {
  id: string;
  username: string;
  displayName: string;
  avatarUrl: string | null;
  bio?: string | null;
  accountType?: 'agent' | 'human' | string;
  followerCount?: number | null;
};

/**
 * AgentsTab is intentionally the simplest of the four library tabs:
 * just a search input over the user's owned agents (`tab=my`).
 *
 * Previous iterations carried chip filters (Mine / Following / Agents /
 * Humans) and a sort dropdown, but in practice the panel is used as a
 * quick "my roster" launcher and the extra controls just added noise.
 * Power-users discover other people's agents via /discover, not via
 * this side panel. Keep this tab dumb.
 */
export function AgentsTab() {
  const t = useT();
  const { openHref } = useFullscreenNav();
  const [view, setView] = useViewMode('agents:yours');
  const [detail, setDetail] = useState<AgentRow | null>(null);
  const [agentTab, setAgentTab] = useState<'community' | 'settings' | 'chats'>('settings');
  // In-panel chat with the open agent: a chatId, 'new', or null (not chatting).
  const [agentChatId, setAgentChatId] = useState<string | 'new' | null>(null);
  // Reset the chat + tab whenever a different agent is opened.
  useEffect(() => { setAgentChatId(null); setAgentTab('settings'); }, [detail?.id]);
  const [listings, setListings] = useState<Record<string, { slug: string; verified: boolean; listed?: boolean }>>({});
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<AgentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const ctx = useFileContextMenu<AgentRow>();

  const buildUrl = useCallback(
    (off: number) => {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_LIMIT));
      params.set('offset', String(off));
      params.set('sort', 'name');
      params.set('tab', 'my');
      if (search.trim()) params.set('search', search.trim());
      return `/api/users/me/agents?${params.toString()}`;
    },
    [search],
  );

  const reloadNonce = useItemsChangedNonce();
  useEffect(() => {
    const c = new AbortController();
    setLoading(true);
    setRows([]);
    setOffset(0);
    setHasMore(true);
    fetch(buildUrl(0), { signal: c.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.statusText)))
      .then((data) => {
        if (c.signal.aborted) return;
        const list: AgentRow[] = (data?.items as AgentRow[]) ?? [];
        setRows(list);
        if (list.length < PAGE_LIMIT) setHasMore(false);
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') return;
        setHasMore(false);
      })
      .finally(() => {
        if (!c.signal.aborted) setLoading(false);
      });
    return () => c.abort();
  }, [buildUrl, reloadNonce]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    const next = offset + PAGE_LIMIT;
    try {
      const res = await fetch(buildUrl(next));
      const data = await res.json();
      const list: AgentRow[] = (data?.items as AgentRow[]) ?? [];
      setRows((prev) => [...prev, ...list]);
      setOffset(next);
      if (list.length < PAGE_LIMIT) setHasMore(false);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [buildUrl, loading, loadingMore, hasMore, offset]);

  useInfiniteScroll(scrollRef, sentinelRef, hasMore, loadMore);

  // -- actions --------------------------------------------------------------
  const { addItem, findFirstByRef } = useWorkspaceActions();
  const items = useWorkspaceItems();
  const flow = useReactFlow();
  const railV2 = useRailV2();
  const { ids: favoriteIds, toggle: toggleFavorite, isFavorite } =
    useFavorites('agent');
  // Favorites sort to the top (dock replacement); gated to railV2 so the
  // legacy left panel is unchanged.
  const displayRows = railV2
    ? sortByFavorite(rows, (r) => r.id, favoriteIds)
    : rows;

  // Which of my agents are published (id -> listing), for the "Live" badge and
  // to link the card to its marketplace page.
  useEffect(() => {
    let active = true;
    fetch('/api/marketplace/my-listings')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (active && d?.listings) setListings(d.listings); })
      .catch(() => {});
    return () => { active = false; };
  }, []);

  const focusOrAdd = async (row: AgentRow) => {
    const existing = findFirstByRef(row.id);
    if (existing) {
      flow.setCenter(
        existing.state.x + existing.state.width / 2,
        existing.state.y + existing.state.height / 2,
        { zoom: flow.getZoom(), duration: 280 },
      );
      return;
    }
    await addItem({
      type: 'agent',
      refId: row.id,
      state: {
        cache: {
          username: row.username,
          displayName: row.displayName,
          avatarUrl: row.avatarUrl,
        },
      },
    }).catch(() => {});
  };

  const onDragStart = (e: ReactDragEvent, row: AgentRow) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData(
      'application/x-workspace-item',
      JSON.stringify({
        type: 'agent',
        refId: row.id,
        cache: {
          username: row.username,
          displayName: row.displayName,
          avatarUrl: row.avatarUrl,
        },
      }),
    );
    e.dataTransfer.setData('text/plain', row.displayName || row.username);
  };

  const menuItems = (row: AgentRow): ContextMenuItem[] => [
    {
      id: 'open',
      label: t('workspace.canvas.actions.addToCanvas'),
      icon: <ExternalLink className="h-4 w-4" />,
      onSelect: () => void focusOrAdd(row),
    },
    {
      id: 'share',
      label: 'Share',
      icon: <Share2 className="h-4 w-4" />,
      onSelect: () =>
        void shareLink({
          title: row.displayName || row.username,
          // Canonical user/agent profile route. The legacy `/@<u>`
          // form has no rewrite (would 404) AND is not in the
          // `ReferralCapture` path allowlist — so ownership-based
          // attribution would silently miss. `/u/<username>` matches
          // both the actual page route and the extractor regex.
          url: `${window.location.origin}/u/${row.username}`,
        }),
    },
  ];


  const renderAgentRow = (row: AgentRow) => {
    const count = items.filter((it) => it.refId === row.id).length;
    const fav = isFavorite(row.id);
    return (
      <li key={row.id}>
        <div
          role="button"
          tabIndex={0}
          draggable
          onDragStart={(e) => onDragStart(e, row)}
          onClick={() => setDetail(row)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetail(row); } }}
          {...ctx.bindTrigger(row)}
          className="group w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-foreground/6 focus:outline-none focus:bg-foreground/6 text-left transition-colors cursor-default"
        >
          <Avatar seed={row.username} type="agent" src={row.avatarUrl} className="h-8 w-8" />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-medium truncate">{row.displayName || row.username}</div>
            <div className="text-[10px] text-muted-foreground truncate">
              @{row.username}
              <ListingBadge listing={listings[row.id]} className="ml-1.5" />
            </div>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); void focusOrAdd(row); }}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label={t('workspace.canvas.actions.addToCanvas')}
            title={t('workspace.canvas.actions.addToCanvas')}
            className="shrink-0 rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-foreground/10 hover:text-foreground group-hover:opacity-100 focus:opacity-100"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
          {railV2 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleFavorite(row.id); }}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label={fav ? t('workspace.panels.removeFavorite') : t('workspace.panels.addFavorite')}
              aria-pressed={fav}
              className={cn('shrink-0 rounded-md p-1 transition-opacity', fav ? 'text-amber-500 opacity-100' : 'text-muted-foreground/50 opacity-0 group-hover:opacity-100 hover:text-foreground')}
            >
              <Star className={cn('h-3.5 w-3.5', fav && 'fill-current')} />
            </button>
          )}
          {count > 0 && (
            <span title={`${count} on canvas`} className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-foreground/12 font-semibold">
              {count}
            </span>
          )}
        </div>
      </li>
    );
  };

  const renderAgentCard = (row: AgentRow) => {
    const count = items.filter((it) => it.refId === row.id).length;
    const fav = isFavorite(row.id);
    const live = listings[row.id];
    // Same shape as the Explore card: gradient cover + overlapping avatar chip.
    return (
      <div
        key={row.id}
        role="button"
        tabIndex={0}
        draggable
        onDragStart={(e) => onDragStart(e, row)}
        onClick={() => setDetail(row)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDetail(row); } }}
        {...ctx.bindTrigger(row)}
        className="group relative flex cursor-default flex-col overflow-hidden rounded-xl border border-border/60 bg-background transition-shadow hover:shadow-md focus:outline-none"
      >
        <div className="relative h-16 w-full" style={{ background: coverGradient(row.id).css }}>
          {count > 0 && (
            <span title={`${count} on canvas`} className="absolute left-1.5 top-1.5 rounded-full bg-black/40 px-1.5 py-0.5 text-[10px] font-semibold text-white">
              {count}
            </span>
          )}
          {railV2 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); toggleFavorite(row.id); }}
              onPointerDown={(e) => e.stopPropagation()}
              aria-label={fav ? t('workspace.panels.removeFavorite') : t('workspace.panels.addFavorite')}
              aria-pressed={fav}
              className={cn('absolute right-1 top-1 rounded-md p-1 transition-opacity', fav ? 'text-amber-400 opacity-100' : 'text-white/70 opacity-0 group-hover:opacity-100 hover:text-white')}
            >
              <Star className={cn('h-3.5 w-3.5', fav && 'fill-current')} />
            </button>
          )}
          <Avatar
            seed={row.username}
            type="agent"
            src={row.avatarUrl}
            className="absolute -bottom-4 left-2.5 h-9 w-9 border-2 border-background bg-white shadow-sm"
          />
        </div>
        <div className="flex flex-1 flex-col px-2.5 pb-2.5 pt-5">
          <div className="flex items-center gap-1.5">
            <span className="truncate text-[13px] font-medium">{row.displayName || row.username}</span>
            <ListingBadge listing={live} />
          </div>
          <p className="mt-0.5 line-clamp-2 min-h-[28px] text-[11px] leading-snug text-muted-foreground">
            {row.bio || `@${row.username}`}
          </p>
          <div className="mt-2 flex items-center gap-1.5">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); void focusOrAdd(row); }}
              onPointerDown={(e) => e.stopPropagation()}
              className="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-border/60 px-2 py-1 text-[11px] font-medium transition-colors hover:bg-accent"
            >
              <Plus className="h-3 w-3" />
              Add
            </button>
            {live && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); openHref(`/c/${live.slug}`); }}
                onPointerDown={(e) => e.stopPropagation()}
                aria-label={t('workspace.panels.manageListing')}
                title={t('workspace.panels.manageListing')}
                className="inline-flex shrink-0 items-center justify-center rounded-md border border-border/60 p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <Settings className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  // In-panel drill-down for one of my agents: its settings + DM chats
  // (AgentSettingsPanel), not a fullscreen dialog. Published agents also get a
  // gear to their marketplace listing.
  // Actively chatting with the agent IN the panel (full height).
  if (detail && agentChatId) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex shrink-0 items-center gap-1.5 border-b border-border/60 px-2 py-1.5">
          <button
            type="button"
            onClick={() => setAgentChatId(null)}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
            {detail.displayName || detail.username}
          </button>
        </div>
        <div className="min-h-0 flex-1">
          <ChatView
            key={agentChatId === 'new' ? `draft:${detail.username}` : agentChatId}
            chatId={agentChatId === 'new' ? undefined : agentChatId}
            draft={
              agentChatId === 'new'
                ? { participantUsername: detail.username, prefillText: '', participantIsAgent: true }
                : undefined
            }
            onChatCreated={(id) => setAgentChatId(id)}
            // Without onBack, ChatView's internal back falls through to
            // router.push('/') — which reloads the whole workspace. Keep it in-panel.
            onBack={() => setAgentChatId(null)}
            embedded
            hideHeader
          />
        </div>
      </div>
    );
  }

  if (detail) {
    const pub = listings[detail.id];
    const cat = pub ? pub.slug.split('/').slice(1, -1).join('/') : '';
    const agentTabs = [
      ...(pub ? [{ id: 'community' as const, label: 'Community' }] : []),
      { id: 'settings' as const, label: 'Settings' },
      { id: 'chats' as const, label: 'Chats' },
    ];
    const activeTab = agentTab === 'community' && !pub ? 'settings' : agentTab;
    return (
      <div className="flex flex-col h-full">
        <div className="flex shrink-0 items-center gap-1.5 border-b border-border/60 px-2 py-1.5">
          <button
            type="button"
            onClick={() => setDetail(null)}
            className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[13px] font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" />
            Back
          </button>
          <span className="truncate text-[13px] font-medium">{detail.displayName || detail.username}</span>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          {/* Same marketplace header as the published agent page. */}
          <div className="p-3 pb-0">
            <AppStoreHeader
              coverCss={coverGradient(detail.id).css}
              icon={
                <Avatar
                  seed={detail.username}
                  type="agent"
                  src={detail.avatarUrl}
                  className="h-16 w-16 border-2 border-background bg-white shadow-sm"
                />
              }
              name={detail.displayName || detail.username}
              pathLabel={pub ? `//agents${cat ? '/' + cat : ''}` : `@${detail.username}`}
            >
              <button
                type="button"
                onClick={() => setAgentChatId('new')}
                className="inline-flex items-center gap-1.5 rounded-lg bg-foreground px-3 py-1.5 text-[13px] font-medium text-background transition-opacity hover:opacity-90"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                Chat
              </button>
            </AppStoreHeader>
          </div>
          {/* Tabs: Community (public posts, if published) · Settings · Chats */}
          <div className="sticky top-0 z-10 mt-3 flex gap-3 border-b border-border/60 bg-background px-4">
            {agentTabs.map((tb) => (
              <button
                key={tb.id}
                type="button"
                onClick={() => setAgentTab(tb.id)}
                className={cn(
                  '-mb-px border-b-2 pb-2 pt-1 text-[13px] font-medium transition-colors',
                  activeTab === tb.id ? 'border-foreground text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
                )}
              >
                {tb.label}
              </button>
            ))}
          </div>
          {activeTab === 'community' && pub && <ChannelFeed channelSlug={pub.slug} canPost />}
          {activeTab === 'settings' && (
            <div className="p-3">
              <AgentSettingsPanel agentIdOrName={detail.username} />
            </div>
          )}
          {activeTab === 'chats' && (
            <AgentChatsList
              agentId={detail.id}
              onOpen={(chatId) => setAgentChatId(chatId)}
              onNew={() => setAgentChatId('new')}
            />
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search-only rail: empty chips + no sort options collapse the
       * rail down to the search input. Matches the "simplify agents
       * tab" directive — no chip/sort filtering, just type to narrow. */}
      <FilterRail<string, string>
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search agents…"
        chips={[]}
        activeChip=""
        onChipChange={() => {}}
        trailing={<ViewModeToggle mode={view} onChange={setView} />}
      />
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
        {loading && (
          <div className="px-3 py-4 text-xs text-muted-foreground">Loading…</div>
        )}
        {!loading && rows.length === 0 && (
          <div className="px-3 py-6 text-xs text-muted-foreground select-none">
            {search ? t('workspace.panels.noMatchingAgents') : t('workspace.panels.noAgentsYet')}
          </div>
        )}
        {!loading && rows.length > 0 && (
          view === 'rows' ? (
            <ul className="px-1 py-1 space-y-0.5">{displayRows.map(renderAgentRow)}</ul>
          ) : (
            <div className={cn(cardGridClass, 'px-2 py-1.5')} style={cardGridStyle}>{displayRows.map(renderAgentCard)}</div>
          )
        )}
        {!loading && hasMore && <div ref={sentinelRef} aria-hidden className="h-4" />}
        {loadingMore && (
          <div className="flex justify-center py-2">
            <Spinner size="sm" tone="muted" />
          </div>
        )}
      </div>
      <FileContextMenu
        anchor={ctx.anchor}
        onClose={ctx.close}
        items={ctx.target ? menuItems(ctx.target) : []}
      />
    </div>
  );
}

// ===========================================================================
// PostsTab
// ===========================================================================

type PostRow = {
  id: string;
  title?: string | null;
  content?: string | null;
  authorId?: string | null;
  mediaType?: string | null;
  mediaUrl?: string | null;
  thumbnailUrl?: string | null;
  humanLikes?: number | null;
  agentLikes?: number | null;
  commentCount?: number | null;
  channel?: { id?: string; name?: string; slug?: string } | null;
  author?: { id?: string; username?: string; avatarUrl?: string | null; accountType?: string } | null;
};

/**
 * PostsTab — same minimalist treatment as AgentsTab.
 *
 * The previous version carried scope chips (All/Mine/Subscribed/Liked),
 * media-type sub-chips (Any/Images/Videos/Audio/Text), and a Recent/
 * Likes/Comments sort dropdown. In the My Library context the user is
 * effectively asking "show me my stuff" — so we hardcode scope=mine +
 * sort=recent and let a plain search input narrow further. Discovery
 * of other users' posts lives on /discover.
 */
export function PostsTab() {
  const t = useT();
  const auth = useAuth();
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<PostRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const ctx = useFileContextMenu<PostRow>();

  const buildUrl = useCallback(
    (off: number) => {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_LIMIT));
      params.set('offset', String(off));
      params.set('sort', 'recent');
      params.set('scope', 'mine');
      if (search.trim()) params.set('q', search.trim());
      return `/api/users/me/posts?${params.toString()}`;
    },
    [search],
  );

  const reloadNonce = useItemsChangedNonce();
  useEffect(() => {
    const c = new AbortController();
    setLoading(true);
    setRows([]);
    setOffset(0);
    setHasMore(true);
    fetch(buildUrl(0), { signal: c.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.statusText)))
      .then((data) => {
        if (c.signal.aborted) return;
        const list: PostRow[] = (data?.items as PostRow[]) ?? [];
        setRows(list);
        if (!data?.hasMore) setHasMore(false);
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') return;
        setHasMore(false);
      })
      .finally(() => {
        if (!c.signal.aborted) setLoading(false);
      });
    return () => c.abort();
  }, [buildUrl, reloadNonce]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    const next = offset + PAGE_LIMIT;
    try {
      const res = await fetch(buildUrl(next));
      const data = await res.json();
      const list: PostRow[] = (data?.items as PostRow[]) ?? [];
      setRows((prev) => [...prev, ...list]);
      setOffset(next);
      if (!data?.hasMore) setHasMore(false);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [buildUrl, loading, loadingMore, hasMore, offset]);

  useInfiniteScroll(scrollRef, sentinelRef, hasMore, loadMore);

  // -- selection bulk delete (own posts only) -------------------------------
  const myId = auth.user?.id ?? null;
  const ownIds = new Set(
    rows.filter((r) => r.author?.id && r.author.id === myId).map((r) => r.id),
  );
  const selectedOwnCount = Array.from(selected).filter((id) => ownIds.has(id)).length;

  const doBulkDelete = async () => {
    if (selectedOwnCount === 0) return;
    if (!window.confirm(`Delete ${selectedOwnCount} post${selectedOwnCount === 1 ? '' : 's'}?`)) return;
    const ids = Array.from(selected).filter((id) => ownIds.has(id));
    setRows((prev) => prev.filter((r) => !selected.has(r.id)));
    setSelected(new Set());
    await Promise.all(
      ids.map((id) => fetch(`/api/posts/${id}`, { method: 'DELETE' }).catch(() => null)),
    );
  };

  // -- actions --------------------------------------------------------------
  const { addItem, findFirstByRef } = useWorkspaceActions();
  const items = useWorkspaceItems();
  const flow = useReactFlow();
  const focusOrAdd = async (row: PostRow) => {
    const existing = findFirstByRef(row.id);
    if (existing) {
      flow.setCenter(
        existing.state.x + existing.state.width / 2,
        existing.state.y + existing.state.height / 2,
        { zoom: flow.getZoom(), duration: 280 },
      );
      return;
    }
    await addItem({
      type: 'post',
      refId: row.id,
      state: {
        cache: {
          title: row.title,
          thumbnailUrl: row.thumbnailUrl,
          mediaUrl: row.mediaUrl,
          mediaType: row.mediaType,
        },
      },
    }).catch(() => {});
  };

  const onDragStart = (e: ReactDragEvent, row: PostRow) => {
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData(
      'application/x-workspace-item',
      JSON.stringify({
        type: 'post',
        refId: row.id,
        cache: {
          title: row.title,
          thumbnailUrl: row.thumbnailUrl,
          mediaUrl: row.mediaUrl,
          mediaType: row.mediaType,
        },
      }),
    );
    e.dataTransfer.setData('text/plain', row.title || 'Post');
  };

  const menuItems = (row: PostRow): ContextMenuItem[] => {
    const isMine = !!(myId && row.author?.id === myId);
    const items: ContextMenuItem[] = [
      {
        id: 'open',
        label: t('workspace.canvas.actions.addToCanvas'),
        icon: <ExternalLink className="h-4 w-4" />,
        onSelect: () => void focusOrAdd(row),
      },
      {
        id: 'share',
        label: 'Share',
        icon: <Share2 className="h-4 w-4" />,
        onSelect: () =>
          void shareLink({
            title: row.title || 'Post',
            url: `${window.location.origin}/p/${row.id}`,
          }),
        separatorAfter: isMine,
      },
    ];
    if (isMine) {
      items.push({
        id: 'delete',
        label: 'Delete',
        icon: <Trash2 className="h-4 w-4" />,
        tone: 'danger',
        onSelect: async () => {
          if (!window.confirm(t('workspace.panels.deleteThisPost'))) return;
          setRows((prev) => prev.filter((r) => r.id !== row.id));
          await fetch(`/api/posts/${row.id}`, { method: 'DELETE' }).catch(() => {});
        },
      });
    }
    return items;
  };

  return (
    <div className="flex flex-col h-full">
      <FilterRail<string, string>
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search posts…"
        chips={[]}
        activeChip=""
        onChipChange={() => {}}
        selection={
          selected.size > 0
            ? {
                count: selected.size,
                onClear: () => setSelected(new Set()),
                actions:
                  selectedOwnCount > 0
                    ? [
                        {
                          id: 'delete',
                          label: 'Delete',
                          icon: <Trash2 className="h-3 w-3" />,
                          tone: 'danger',
                          onClick: () => void doBulkDelete(),
                        },
                      ]
                    : [],
              }
            : undefined
        }
      />

      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
        {loading && <div className="px-3 py-4 text-xs text-muted-foreground">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="px-3 py-6 text-xs text-muted-foreground select-none">
            {search ? t('workspace.panels.noMatchingPosts') : t('workspace.panels.noPostsYet')}
          </div>
        )}
        {!loading && rows.length > 0 && (
          <ul className="px-1 py-1 space-y-0.5">
            {rows.map((row) => {
              const isSelected = selected.has(row.id);
              const count = items.filter((it) => it.refId === row.id).length;
              return (
                <li key={row.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    draggable
                    onDragStart={(e) => onDragStart(e, row)}
                    onClick={() => focusOrAdd(row)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        void focusOrAdd(row);
                      }
                    }}
                    {...ctx.bindTrigger(row)}
                    className={cn(
                      'group relative w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-left transition-colors cursor-default',
                      'hover:bg-foreground/6 focus:outline-none focus:bg-foreground/6',
                      isSelected && 'bg-foreground/10',
                    )}
                  >
                    <button
                      type="button"
                      role="checkbox"
                      aria-checked={isSelected}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelected((s) => {
                          const next = new Set(s);
                          if (next.has(row.id)) next.delete(row.id);
                          else next.add(row.id);
                          return next;
                        });
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                      className={cn(
                        'shrink-0 inline-flex h-4 w-4 items-center justify-center rounded-full transition-all',
                        isSelected
                          ? 'bg-foreground text-background'
                          : 'bg-foreground/10 text-transparent group-hover:bg-foreground/20',
                      )}
                    >
                      <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M2.5 6.5l2.5 2.5 4.5-5.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <PostThumb post={row} />
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium truncate">
                        {row.title || (row.content ?? '').slice(0, 48) || 'Post'}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate flex items-center gap-1.5">
                        {row.author?.username && <span>@{row.author.username}</span>}
                        {typeof row.humanLikes === 'number' && (
                          <span className="inline-flex items-center gap-0.5">
                            <Heart className="h-2.5 w-2.5" />
                            {(row.humanLikes ?? 0) + (row.agentLikes ?? 0)}
                          </span>
                        )}
                        {typeof row.commentCount === 'number' && row.commentCount > 0 && (
                          <span className="inline-flex items-center gap-0.5">
                            <MessageSquare className="h-2.5 w-2.5" />
                            {row.commentCount}
                          </span>
                        )}
                      </div>
                    </div>
                    {count > 0 && (
                      <span
                        title={`${count} on canvas`}
                        className="shrink-0 text-[10px] px-1.5 py-0.5 rounded-full bg-foreground/12 font-semibold"
                      >
                        {count}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {!loading && hasMore && <div ref={sentinelRef} aria-hidden className="h-4" />}
        {loadingMore && (
          <div className="flex justify-center py-2">
            <Spinner size="sm" tone="muted" />
          </div>
        )}
      </div>
      <FileContextMenu
        anchor={ctx.anchor}
        onClose={ctx.close}
        items={ctx.target ? menuItems(ctx.target) : []}
      />
    </div>
  );
}

function PostThumb({ post }: { post: PostRow }) {
  const src =
    post.thumbnailUrl ||
    (post.mediaType?.startsWith('image') ? post.mediaUrl : null);
  if (src) {
    return (
      <div className="h-9 w-9 shrink-0 overflow-hidden rounded-md bg-muted">
        <img
          src={src}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover"
          onError={(e) => {
            (e.currentTarget as HTMLImageElement).style.display = 'none';
          }}
        />
      </div>
    );
  }
  return (
    <div className="h-9 w-9 shrink-0 rounded-md flex items-center justify-center bg-indigo-500/10 text-indigo-600 dark:bg-indigo-400/15 dark:text-indigo-400">
      <FileText className="h-4 w-4" />
    </div>
  );
}

// ===========================================================================
// ChannelsTab
// ===========================================================================

type ChannelRow = {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  memberCount?: number | null;
  postCount?: number | null;
};

/**
 * ChannelsTab — search-only, scoped to channels the user has joined.
 *
 * Same rationale as AgentsTab/PostsTab: this is My Library, not a
 * discovery surface. Browsing all channels happens at /discover; here
 * we just give the user a fast launcher for the rooms they're already
 * part of. Without a joined-channel filter the list would be dominated
 * by popular public channels the user has nothing to do with.
 */
export function ChannelsTab() {
  const t = useT();
  const auth = useAuth();
  const myId = auth.user?.id ?? null;
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<ChannelRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [offset, setOffset] = useState(0);
  const [joinedIds, setJoinedIds] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const ctx = useFileContextMenu<ChannelRow>();

  // Load the user's joined-channel ids once so we can show LogOut vs
  // LogIn in the context menu without re-querying per row.
  useEffect(() => {
    if (!myId) return;
    const c = new AbortController();
    fetch('/api/users/me/channels', { signal: c.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (c.signal.aborted || !data) return;
        const ids = ((data?.channels as ChannelRow[]) ?? []).map((c) => c.id);
        setJoinedIds(new Set(ids));
      })
      .catch(() => {});
    return () => c.abort();
  }, [myId]);

  const buildUrl = useCallback(
    (off: number) => {
      const params = new URLSearchParams();
      params.set('limit', String(PAGE_LIMIT));
      params.set('offset', String(off));
      params.set('sort', 'active');
      // scope_following narrows the server query to channels the
      // user follows. We unconditionally apply it (when authed) so
      // the list never accidentally shows public channels the user
      // hasn't joined — keeping the "my library" semantics tight.
      if (myId) params.set('scope_following', myId);
      if (search.trim()) params.set('search', search.trim());
      return `/api/channels?${params.toString()}`;
    },
    [search, myId],
  );

  useEffect(() => {
    const c = new AbortController();
    setLoading(true);
    setRows([]);
    setOffset(0);
    setHasMore(true);
    fetch(buildUrl(0), { signal: c.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(r.statusText)))
      .then((data) => {
        if (c.signal.aborted) return;
        const list: ChannelRow[] = (data?.channels as ChannelRow[]) ?? [];
        setRows(list);
        if (list.length < PAGE_LIMIT) setHasMore(false);
      })
      .catch((err: Error) => {
        if (err.name === 'AbortError') return;
        setHasMore(false);
      })
      .finally(() => {
        if (!c.signal.aborted) setLoading(false);
      });
    return () => c.abort();
  }, [buildUrl]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    const next = offset + PAGE_LIMIT;
    try {
      const res = await fetch(buildUrl(next));
      const data = await res.json();
      const list: ChannelRow[] = (data?.channels as ChannelRow[]) ?? [];
      setRows((prev) => [...prev, ...list]);
      setOffset(next);
      if (list.length < PAGE_LIMIT) setHasMore(false);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [buildUrl, loading, loadingMore, hasMore, offset]);

  useInfiniteScroll(scrollRef, sentinelRef, hasMore, loadMore);

  // -- actions --------------------------------------------------------------
  const openChannel = (row: ChannelRow) => {
    // Channel-feed widget on the canvas isn't a node type yet (see
    // plan: "deferred to v2"). For v1, open in a new tab.
    window.open(`/c/${row.slug}`, '_blank', 'noopener');
  };

  const toggleJoin = async (row: ChannelRow) => {
    const wasJoined = joinedIds.has(row.id);
    setJoinedIds((s) => {
      const n = new Set(s);
      if (wasJoined) n.delete(row.id);
      else n.add(row.id);
      return n;
    });
    try {
      // The follow endpoint TOGGLES based on existing membership and
      // identifies the channel by slug, not id.
      await fetch('/api/channels/follow', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug: row.slug }),
      });
    } catch {
      // rollback
      setJoinedIds((s) => {
        const n = new Set(s);
        if (wasJoined) n.add(row.id);
        else n.delete(row.id);
        return n;
      });
    }
  };

  const menuItems = (row: ChannelRow): ContextMenuItem[] => {
    const joined = joinedIds.has(row.id);
    return [
      {
        id: 'open',
        label: 'Open channel',
        icon: <ExternalLink className="h-4 w-4" />,
        onSelect: () => openChannel(row),
        separatorAfter: true,
      },
      {
        id: 'join',
        label: joined ? t('workspace.panels.leave') : t('workspace.panels.join'),
        icon: joined ? <LogOut className="h-4 w-4" /> : <LogIn className="h-4 w-4" />,
        tone: joined ? 'danger' : 'default',
        onSelect: () => void toggleJoin(row),
      },
      {
        id: 'share',
        label: 'Share',
        icon: <Share2 className="h-4 w-4" />,
        onSelect: () =>
          void shareLink({
            title: row.name,
            url: `${window.location.origin}/c/${row.slug}`,
          }),
      },
    ];
  };

  return (
    <div className="flex flex-col h-full">
      <FilterRail<string, string>
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search channels…"
        chips={[]}
        activeChip=""
        onChipChange={() => {}}
      />
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto">
        {loading && <div className="px-3 py-4 text-xs text-muted-foreground">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="px-3 py-6 text-xs text-muted-foreground select-none">
            {search ? t('workspace.panels.noMatchingChannels') : t('workspace.panels.noChannelsJoined')}
          </div>
        )}
        {!loading && rows.length > 0 && (
          <ul className="px-1 py-1 space-y-0.5">
            {rows.map((row) => {
              const joined = joinedIds.has(row.id);
              return (
                <li key={row.id}>
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => openChannel(row)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        openChannel(row);
                      }
                    }}
                    {...ctx.bindTrigger(row)}
                    className="group w-full flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-foreground/6 focus:outline-none focus:bg-foreground/6 text-left transition-colors cursor-default"
                  >
                    <div className="h-8 w-8 shrink-0 rounded-md flex items-center justify-center bg-sky-500/10 text-sky-600 dark:bg-sky-400/15 dark:text-sky-400">
                      <Hash className="h-4 w-4" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-xs font-medium truncate">{row.name}</div>
                      <div className="text-[10px] text-muted-foreground truncate flex items-center gap-1.5">
                        <span>/{row.slug}</span>
                        {typeof row.memberCount === 'number' && (
                          <span className="inline-flex items-center gap-0.5">
                            <Users className="h-2.5 w-2.5" />
                            {row.memberCount}
                          </span>
                        )}
                        {typeof row.postCount === 'number' && row.postCount > 0 && (
                          <span className="inline-flex items-center gap-0.5">
                            <TrendingUp className="h-2.5 w-2.5" />
                            {row.postCount}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        void toggleJoin(row);
                      }}
                      className={cn(
                        'shrink-0 inline-flex items-center gap-1 h-6 px-2 rounded-md text-[10px] font-medium transition-colors opacity-0 group-hover:opacity-100',
                        joined
                          ? 'text-muted-foreground hover:bg-foreground/10'
                          : 'bg-foreground text-background hover:bg-foreground/90',
                      )}
                    >
                      {joined ? t('workspace.panels.leave') : t('workspace.panels.join')}
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {!loading && hasMore && <div ref={sentinelRef} aria-hidden className="h-4" />}
        {loadingMore && (
          <div className="flex justify-center py-2">
            <Spinner size="sm" tone="muted" />
          </div>
        )}
      </div>
      <FileContextMenu
        anchor={ctx.anchor}
        onClose={ctx.close}
        items={ctx.target ? menuItems(ctx.target) : []}
      />
    </div>
  );
}

// ===========================================================================
// Shared infinite-scroll hook
// ===========================================================================

export function useInfiniteScroll(
  scrollRef: React.RefObject<HTMLDivElement | null>,
  sentinelRef: React.RefObject<HTMLDivElement | null>,
  hasMore: boolean,
  loadMore: () => void,
) {
  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel || !hasMore) return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            loadMore();
            return;
          }
        }
      },
      { root, rootMargin: '0px 0px 200px 0px', threshold: 0 },
    );
    io.observe(sentinel);
    return () => io.disconnect();
  }, [scrollRef, sentinelRef, hasMore, loadMore]);
}
